import { questionColumns } from "./-components/question-columns"
import { AssignDeckDialog } from "./-components/assign-deck-dialog"
import { CreateQuestionDialog } from "./-components/create-question-dialog"
import { RenameDeckDialog } from "../-components/rename-deck-dialog"
import { DeleteDeckDialog } from "../-components/delete-deck-dialog"
import { useState } from "react"
import { createFileRoute, Link } from "@tanstack/react-router"
import { z } from "zod"
import { useQuery } from "convex/react"
import { api } from "@convex/_generated/api"
import type { Id } from "@convex/_generated/dataModel"
import { Button } from "@/components/ui/button"
import { DataTable } from "@/components/ui/data-table"
import { Skeleton } from "@/components/ui/skeleton"
import { Badge } from "@/components/ui/badge"
import { AlertCircle, Pencil, Plus, Trash2 } from "lucide-react"

const deckDetailSearchSchema = z.object({
  question: z.string().optional().catch(undefined),
})

export const Route = createFileRoute("/(admin)/admin/decks/$deckId/")({
  component: RouteComponent,
  validateSearch: deckDetailSearchSchema,
})

function RouteComponent() {
  const navigate = Route.useNavigate()
  const { deckId } = Route.useParams()
  const { question } = Route.useSearch()
  const [assignDialogOpen, setAssignDialogOpen] = useState(false)
  const [createQuestionOpen, setCreateQuestionOpen] = useState(false)
  const [renameDialogOpen, setRenameDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const deck = useQuery(api.decks.handlers.getById, {
    deckId: deckId as Id<"decks">,
  })
  const questions = useQuery(api.decks.handlers.listQuestionsForDeck, {
    deckId: deckId as Id<"decks">,
  })
  const assignedQuiz = useQuery(
    api.quizzes.handlers.getById,
    deck?.quizId ? { quizId: deck.quizId } : "skip"
  )

  const handleQuestionFilterChange = (value: string) => {
    navigate({
      search: (prev) => ({
        ...prev,
        question: value || undefined,
      }),
    }).catch((error: unknown) => {
      console.error("Navigation error:", error)
    })
  }

  if (!deck || !questions) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    )
  }

  return (
    <div className="flex flex-1 flex-col gap-4 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{deck.name}</h1>
          <p className="text-sm text-muted-foreground">
            {questions.length} questions
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setRenameDialogOpen(true)
            }}
          >
            <Pencil className="h-4 w-4 mr-2" />
            Rename
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => {
              setDeleteDialogOpen(true)
            }}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setCreateQuestionOpen(true)
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Question
          </Button>
          {deck.quizId && deck.roundNumber !== undefined ? (
            <>
              <Badge variant="default" className="bg-green-500">
                Assigned to Round {deck.roundNumber}
              </Badge>
              {assignedQuiz && (
                <Button variant="outline" size="sm" asChild>
                  <Link
                    to="/admin/quizzes/$quizId"
                    params={{ quizId: deck.quizId as string }}
                  >
                    View Quiz
                  </Link>
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setAssignDialogOpen(true)
                }}
              >
                Reassign
              </Button>
            </>
          ) : (
            <Button
              onClick={() => {
                setAssignDialogOpen(true)
              }}
            >
              Assign to Round
            </Button>
          )}
        </div>
      </div>

      {deck.quizId && assignedQuiz && (
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
              This deck is assigned to "{assignedQuiz.name}" - Round{" "}
              {deck.roundNumber}
            </p>
            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
              Questions from this deck are used in that quiz round.
            </p>
          </div>
        </div>
      )}

      <DataTable
        columns={questionColumns}
        data={questions}
        filterColumn="text"
        filterPlaceholder="Filter by question text..."
        filterValue={question}
        onFilterChange={handleQuestionFilterChange}
      />

      <AssignDeckDialog
        deckId={deckId as Id<"decks">}
        currentQuizId={deck.quizId}
        currentRoundNumber={deck.roundNumber}
        open={assignDialogOpen}
        onOpenChange={setAssignDialogOpen}
      />

      <CreateQuestionDialog
        deckId={deckId as Id<"decks">}
        open={createQuestionOpen}
        onOpenChange={setCreateQuestionOpen}
      />

      <RenameDeckDialog
        deck={deck}
        open={renameDialogOpen}
        onOpenChange={setRenameDialogOpen}
      />
      <DeleteDeckDialog
        deck={deck}
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
      />
    </div>
  )
}
